import type React from "react"
import { useEffect, useState } from "react"
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Avatar,
  Divider,
  CircularProgress,
  Button,
  Alert,
} from "@mui/material"
import { requestService } from "../../../services/CrmRequestService"
import { useNavigate } from "react-router-dom"
import type { RequestDto } from "../../../types/crm/types/request"
import { useAuth } from "../../../context/AuthContext"

interface RequestListProps {
  userProfileId: number
  userRole: string
  statusFilter?: "all" | "pending" | "approved" | "rejected"
}

const RequestList: React.FC<RequestListProps> = ({ userProfileId, userRole, statusFilter = "all" }) => {
  const [requests, setRequests] = useState<RequestDto[]>([]);
  const [filteredRequests, setFilteredRequests] = useState<RequestDto[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { userToken } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const profiles = userToken?.user.profiles || [];

    const fetchRequests = async () => {
      try {
        setLoading(true)

        let data: RequestDto[] = []

        if (userRole === "Broker") {
          // Buscar el perfil activo para obtener el ID del realtor
          const activeProfile = profiles?.find((profile) => profile.id === userProfileId)

          if (activeProfile && activeProfile.realtor) {
            // Usar el ID del realtor para obtener las solicitudes
            const realtorId = activeProfile.realtor.id
            data = await requestService.getRequestsByBroker(realtorId)
          } else {
            console.error("No se encontró información del realtor para el perfil activo")
            throw new Error("No se encontró información del realtor para el perfil activo")
          }
        } else if (userRole === "Cliente") {
          data = await requestService.getRequestsByClient(userProfileId)
        } else {
          throw new Error(`El rol "${userRole}" no está configurado para ver solicitudes`)
        }

        const cleanedData = data.map((request) => ({
          ...request,
          propertyPrice: request.propertyPrice || 0,
          propertyTitle: request.propertyTitle || "Sin título",
          propertyAddress: request.propertyAddress || "Sin dirección",
          statusCode: request.statusCode || "Pendiente",
          statusColor: request.statusColor || "#999",
          userFullName: request.userFullName || "Usuario",
          message: request.message || "",
        }))

        setRequests(cleanedData)
        setError(null)
      } catch (err: any) {
        console.error("Error en fetchRequests:", err)
        setError(err.message || "Error al cargar las solicitudes")
      } finally {
        setLoading(false)
      }
    }

    let isMounted = true

    if (userProfileId) {
      const timer = setTimeout(() => {
        if (isMounted) {
          fetchRequests()
        }
      }, 300)

      return () => {
        isMounted = false
        clearTimeout(timer)
      }
    }

    return () => {
      isMounted = false
    }
  }, [userProfileId, userRole, userToken?.user.profiles])

  useEffect(() => {
    if (requests.length === 0) {
      setFilteredRequests([])
      return
    }

    let filtered = [...requests]

    if (statusFilter !== "all") {
      filtered = requests.filter((request) => {
        const statusCode = (request.statusCode || "").toLowerCase()
        const moduleStateId = request.moduleStateId

        switch (statusFilter) {
          case "pending":
            return statusCode === "pendiente" || moduleStateId === 4
          case "approved":
            return statusCode === "aprobada" || moduleStateId === 6
          case "rejected":
            return statusCode === "rechazada" || moduleStateId === 5
          default:
            return true
        }
      })
    }
    setFilteredRequests(filtered)
  }, [requests, statusFilter])

  const handleViewDetails = (requestId: number) => {
    navigate(`${requestId}`)
  }

  const formatPrice = (price: number | null | undefined): string => {
    if (price === null || price === undefined) {
      return "$0"
    }
    return `$${price.toLocaleString()}`
  }

  // Función para obtener el color correcto según el estado
  const getStatusColor = (request: RequestDto): string => {
    // Si ya tenemos un color definido, usarlo
    if (request.statusColor) return request.statusColor

    // Si no, determinar el color según el moduleStateId
    switch (request.moduleStateId) {
      case 4:
        return "#FFA500" // Pendiente
      case 5:
        return "#FF0000" // Rechazada
      case 6:
        return "#00FF00" // Aprobada
      default:
        return "#999" // Color por defecto
    }
  }

  // Función para obtener el nombre del estado correcto
  const getStatusName = (request: RequestDto): string => {
    // Si ya tenemos un nombre definido, usarlo
    if (request.statusCode) return request.statusCode

    // Si no, determinar el nombre según el moduleStateId
    switch (request.moduleStateId) {
      case 4:
        return "Pendiente"
      case 5:
        return "Rechazada"
      case 6:
        return "Aprobada"
      default:
        return "Desconocido"
    }
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <Alert severity="info" sx={{ width: "100%" }}>
          {error}
          <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              Por favor, cambia de perfil para poder ver esta información.
          </Typography>
        </Alert>
      </Box>
    )
  }

  if (filteredRequests.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <Alert severity="info" sx={{ width: "100%" }}>
          No hay solicitudes disponibles
          <Typography variant="caption" display="block" sx={{ mt: 1 }}>
            Filtro actual: {statusFilter}, Total de solicitudes: {requests.length}
          </Typography>
        </Alert>
      </Box>
    )
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Solicitudes ({filteredRequests.length})
      </Typography>
      <Grid container spacing={2}>
        {filteredRequests.map((request) => (
          <Grid item xs={12} sm={6} lg={4} key={request.id}>
            <Card
              elevation={3}
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                transition: "transform 0.2s",
                "&:hover": {
                  transform: "translateY(-5px)",
                  boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
                },
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  height: { xs: 120, sm: 140 },
                  backgroundImage: `url(${request.propertyImage || "/placeholder-property.jpg"})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              >
                <Chip
                  label={getStatusName(request)}
                  sx={{
                    position: "absolute",
                    top: 10,
                    right: 10,
                    backgroundColor: getStatusColor(request),
                    color: "#fff",
                    fontSize: { xs: "0.7rem", sm: "0.8rem" },
                    height: { xs: 24, sm: 32 },
                  }}
                />
              </Box>
              <CardContent sx={{ flexGrow: 1, p: { xs: 2, sm: 3 } }}>
                <Typography
                  variant="h6"
                  noWrap
                  title={request.propertyTitle || "Sin título"}
                  sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}
                >
                  {request.propertyTitle || "Sin título"}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom noWrap>
                  {request.propertyAddress || "Sin dirección"}
                </Typography>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}>
                  {formatPrice(request.propertyPrice)}
                </Typography>

                <Divider sx={{ my: 1.5 }} />

                <Box display="flex" alignItems="center" mb={1.5}>
                  <Avatar
                    src={request.userProfileImage || "/placeholder-user.jpg"}
                    sx={{ width: { xs: 24, sm: 32 }, height: { xs: 24, sm: 32 }, mr: 1 }}
                  />
                  <Box>
                    <Typography
                      variant="body2"
                      fontWeight="medium"
                      sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                    >
                      {request.userFullName || "Usuario"}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }}
                    >
                      {request.createdDate ? new Date(request.createdDate).toLocaleDateString() : "Fecha desconocida"}
                    </Typography>
                  </Box>
                </Box>

                <Typography
                  variant="body2"
                  noWrap
                  title={request.message || "Sin mensaje"}
                  sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                >
                  {request.message || "Sin mensaje"}
                </Typography>

                <Button
                  variant="outlined"
                  fullWidth
                  sx={{
                    mt: 2,
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    py: { xs: 0.5, sm: 1 },
                  }}
                  onClick={() => handleViewDetails(request.id)}
                >
                  Ver detalles
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  )
}

export default RequestList